import { useMemo, useSyncExternalStore } from 'react';
import HistoryOutlined from '@mui/icons-material/HistoryOutlined';
import { recentRecordsStore } from '@/components/GlobalSearch/commands/recentRecordsStore';
import type { SlashCommand } from '@/components/GlobalSearch/commands';

const subscribe = (cb: () => void) => recentRecordsStore.subscribe(cb);
const snapshot = () => recentRecordsStore.getRecent();

/**
 * Palette entries for records the user has recently opened.
 *
 * Deliberately unlocalized here: the title is the record's own business identity, and the
 * subtitle goes through the existing `DisplayName:` keys, so this hook never touches `t`.
 * That keeps the returned array stable across renders — an unstable one cascades into
 * `slash.flat` and breaks the palette's arrow-key navigation.
 */
export function useRecentRecordCommands(): SlashCommand[] {
  const records = useSyncExternalStore(subscribe, snapshot);

  return useMemo(
    () =>
      records.map((record) => ({
        id: `history.${record.entityType}.${record.entityId}`,
        // Falls back to a short id when the label was never resolved, so a visit is still
        // navigable rather than rendering as a blank row.
        title: { value: record.label || `${record.entityType} ${record.entityId.slice(0, 8)}` },
        subtitle: { key: `DisplayName:${record.entityType}`, value: record.entityType },
        group: 'history' as const,
        icon: HistoryOutlined,
        // '/history' should land here directly; the Swedish alias matters because the UI
        // language changes but this list is built without translation.
        keywords: ['history', 'historik', record.entityType, record.label ?? ''].filter(Boolean),
        run: (ctx) => {
          ctx.closeDialog();
          ctx.navigate(record.path);
        },
      })),
    [records],
  );
}
